import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Info, ShieldAlert } from 'lucide-react';
import { OkResponse, SupportSessionInfo } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCaps } from '../../lib/session';
import { Button } from '../../components/ui';

/** Red strip while a CloudSign support session is open in this organisation (core-phase4 s3). */
function SupportBanner() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['support-session'],
    queryFn: () => api.get('/auth/support-session', { schema: SupportSessionInfo.nullable() }),
    refetchInterval: 60_000,
  });
  const end = useMutation({
    mutationFn: () => api.post('/auth/support-session/end', { schema: OkResponse }),
    onSuccess: () => {
      // The session goes back to the platform area, so everything on screen is stale.
      qc.removeQueries();
      void navigate('/', { replace: true });
    },
  });
  const s = q.data;
  if (!s) return null;
  return (
    <div role="region" aria-label={t('banner.support.label')} className="border-b border-red-800 bg-red-700 text-white">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 text-sm sm:px-6">
        <ShieldAlert aria-hidden="true" className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1">
          {t('banner.support.text', { name: s.supportLabel, tenant: s.tenantName, time: formatDateTime(s.endsAt), reason: s.reason })}
        </p>
        <Button className="border-white bg-white !text-red-800 hover:bg-red-50" disabled={end.isPending} onClick={() => end.mutate()}>
          {t('banner.support.end')}
        </Button>
      </div>
    </div>
  );
}

/** Amber strip when billing has put the organisation into a restriction (ADR 0031). */
function RestrictionBanner() {
  const caps = useCaps();
  const r = caps.restriction;
  if (!r || r === 'none') return null;
  return (
    <div role="region" aria-label={t('banner.restriction.label')} className="border-b border-amber-300 bg-amber-50 text-amber-950">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2 text-sm sm:px-6">
        <Info aria-hidden="true" className="h-4 w-4 shrink-0" />
        <p>{t(`banner.restriction.${r}`)}</p>
      </div>
    </div>
  );
}

/** Mounted in the Shell's banner slot, under the top bar. */
export function ShellBanners() {
  const caps = useCaps();
  if (caps.area !== 'tenant') return null;
  return (
    <>
      <SupportBanner />
      <RestrictionBanner />
    </>
  );
}
